import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Badge } from '@/components/data-display/badge'
import { DataTable, type DataTableColumn } from '@/components/data-display/data-table'
import { DataTablePrimaryCell } from '@/components/data-display/data-table-primary-cell'
import { useFeedback } from '@/components/feedback/feedback-context'
import { Input } from '@/components/forms/input'
import { PageHeader } from '@/components/layout/page-header'
import { ActionMenu } from '@/components/overlays/action-menu'
import { ConfirmationDialog } from '@/components/overlays/confirmation-dialog'
import { Button } from '@/components/primitives/button'
import actionDeleteIconUrl from '@/assets/action-menu/delete.svg'
import actionEditIconUrl from '@/assets/action-menu/edit.svg'
import { AdminShellPage } from '@/features/admin/shell/shell-page'

import {
  createOidcClientScope,
  deleteOidcClientScope,
  listOidcClientScopes,
  type OidcClientScope,
} from './api'
import { OidcClientScopeFormDrawer } from './idp-client-scope-form-drawer'
import { IdpConfigurationFlow } from './idp-configuration-flow'
import { isOidcIdpReadOnly } from './idp-permissions'

const queryKey = ['admin-idp-client-scopes']

export function AdminOidcClientScopesPage() {
  const { t } = useTranslation('adminIdp')
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { showFeedback } = useFeedback()
  const readOnly = isOidcIdpReadOnly()
  const [search, setSearch] = useState('')
  const [createOpen, setCreateOpen] = useState(false)
  const [pendingDeleteScope, setPendingDeleteScope] = useState<OidcClientScope | null>(null)
  const scopesQuery = useQuery({ queryFn: listOidcClientScopes, queryKey })
  const createMutation = useMutation({
    mutationFn: createOidcClientScope,
    onError: () => showFeedback({ message: t('clientScopes.errors.create'), tone: 'error' }),
    onSuccess: (scope) => {
      void queryClient.invalidateQueries({ queryKey })
      setCreateOpen(false)
      showFeedback({ message: t('clientScopes.success.created'), tone: 'success' })
      void navigate({
        params: { clientScopeId: scope.id },
        to: '/admin/idp/client-scopes/$clientScopeId',
      })
    },
  })
  const deleteMutation = useMutation({
    mutationFn: deleteOidcClientScope,
    onError: () => showFeedback({ message: t('clientScopes.errors.delete'), tone: 'error' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey })
      setPendingDeleteScope(null)
      showFeedback({ message: t('clientScopes.success.deleted'), tone: 'success' })
    },
  })

  const rows = useMemo(() => {
    const normalized = search.trim().toLocaleLowerCase()
    if (!normalized) return scopesQuery.data ?? []
    return (scopesQuery.data ?? []).filter((scope) =>
      [scope.name, scope.display_name, scope.description]
        .join(' ')
        .toLocaleLowerCase()
        .includes(normalized),
    )
  }, [scopesQuery.data, search])

  const columns = useMemo<DataTableColumn<OidcClientScope>[]>(
    () => [
      {
        header: t('clientScopes.columns.name'),
        id: 'name',
        render: (scope) => (
          <button
            className="group max-w-full cursor-pointer text-left"
            onClick={() =>
              void navigate({
                params: { clientScopeId: scope.id },
                to: '/admin/idp/client-scopes/$clientScopeId',
              })
            }
            type="button"
          >
            <DataTablePrimaryCell
              subtitle={<code>{scope.name}</code>}
              title={scope.display_name}
              titleClassName="transition-colors group-hover:text-[var(--color-primary)]"
            />
          </button>
        ),
        width: 260,
      },
      {
        header: t('clientScopes.columns.description'),
        id: 'description',
        render: (scope) => (
          <span className="line-clamp-1 text-[var(--color-text-muted)]">
            {scope.description || '—'}
          </span>
        ),
        width: 340,
      },
      {
        align: 'center',
        header: t('clientScopes.columns.mappers'),
        id: 'mappers',
        render: (scope) => scope.mapper_count,
        width: 90,
      },
      {
        header: t('clientScopes.columns.type'),
        id: 'type',
        render: (scope) => (
          <Badge variant={scope.system ? 'neutral' : 'info'}>
            {scope.system ? t('clientScopes.builtIn') : t('clientScopes.custom')}
          </Badge>
        ),
        width: 100,
      },
      {
        header: t('clientScopes.columns.status'),
        id: 'status',
        render: (scope) => (
          <Badge variant={scope.enabled ? 'success' : 'neutral'}>
            {scope.enabled ? t('status.enabled') : t('status.disabled')}
          </Badge>
        ),
        width: 100,
      },
      {
        align: 'right',
        header: t('clientScopes.columns.actions'),
        id: 'actions',
        pin: 'right',
        render: (scope) => (
          <ActionMenu
            ariaLabel={t('clientScopes.rowActions.open', { name: scope.display_name })}
            items={[
              {
                iconSrc: actionEditIconUrl,
                key: 'edit',
                label: t('actions.edit'),
                onSelect: () =>
                  void navigate({
                    params: { clientScopeId: scope.id },
                    to: '/admin/idp/client-scopes/$clientScopeId',
                  }),
              },
              ...(!scope.system && !readOnly
                ? [
                    {
                      disabled: deleteMutation.isPending,
                      iconSrc: actionDeleteIconUrl,
                      key: 'delete',
                      label: t('actions.delete'),
                      onSelect: () => setPendingDeleteScope(scope),
                      variant: 'danger' as const,
                    },
                  ]
                : []),
            ]}
          />
        ),
        width: 90,
      },
    ],
    [deleteMutation, navigate, readOnly, t],
  )

  return (
    <AdminShellPage activePath="/admin/idp/client-scopes">
      <div className="flex h-full flex-col overflow-hidden bg-white">
        <PageHeader
          actions={
            readOnly ? null : (
              <Button onClick={() => setCreateOpen(true)}>{t('clientScopes.create')}</Button>
            )
          }
          description={t('clientScopes.description')}
          title={t('clientScopes.title')}
        />
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 lg:px-[30px]">
          <div className="mb-5">
            <IdpConfigurationFlow active="scopes" />
          </div>
          <div className="mb-4 flex max-w-[420px] items-center gap-2">
            <Input
              aria-label={t('clientScopes.search')}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t('clientScopes.search')}
              value={search}
            />
          </div>
          {scopesQuery.isError ? (
            <p className="text-sm text-[var(--color-danger)]">{t('clientScopes.errors.load')}</p>
          ) : (
            <DataTable
              columns={columns}
              empty={
                <div className="py-10 text-center text-sm text-[var(--color-text-muted)]">
                  {t('clientScopes.empty')}
                </div>
              }
              getRowKey={(scope) => scope.id}
              isLoading={scopesQuery.isLoading}
              minWidth={940}
              rows={rows}
            />
          )}
        </div>
      </div>

      {readOnly ? null : (
        <OidcClientScopeFormDrawer
          isSaving={createMutation.isPending}
          key={createOpen ? 'open' : 'closed'}
          onOpenChange={setCreateOpen}
          onSave={(payload) => createMutation.mutate(payload)}
          open={createOpen}
        />
      )}
      <ConfirmationDialog
        cancelLabel={t('actions.cancel')}
        confirmLabel={t('actions.delete')}
        description={t('clientScopes.confirmDelete', {
          name: pendingDeleteScope?.display_name ?? '',
        })}
        isConfirming={deleteMutation.isPending}
        onCancel={() => setPendingDeleteScope(null)}
        onConfirm={() => {
          if (pendingDeleteScope) deleteMutation.mutate(pendingDeleteScope.id)
        }}
        open={Boolean(pendingDeleteScope)}
        title={t('clientScopes.confirmDeleteTitle')}
        tone="danger"
      />
    </AdminShellPage>
  )
}
